<template>
  <VOffCanvas
    id="the-case-pax-details-off-canvas"
    :backdrop-static="true"
    offcanvas-class="offcanvas-end"
    @closed="closeOffCanvas"
  >
    <ResizableContainer>
      <OffCanvasHeader>
        <div class="d-flex align-items-center">
          <v-icon class="text-primary" icon="info" size="xxl" space="me-8" />

          <h5 id="the-case-pax-details-off-canvas-label" class="offcanvas-title">
            {{ $t("tourBuilder.paxDetails") }}
          </h5>
        </div>
      </OffCanvasHeader>

      <OffCanvasBody class="w-100">
        <div class="mb-0 search-separator-container">
          <div class="separator-text-container">
            <span class="text-sm separator-text fw-semi">Pax List</span>
          </div>
        </div>
        <template v-if="availablePax.length > 0 || paxFields.length > 0">
          <div class="pax-cards-container mt-12 mb-16">
            <!-- Existing Pax -->
            <div v-for="(p, index) in availablePax" :key="'avail-' + index" class="pax-item d-flex align-items-center mb-8 border rounded-xs overflow-hidden bg-light bg-opacity-10 transition-all">
              <div class="pax-icon bg-primary text-white d-flex align-items-center justify-content-center fw-bold" style="width: 32px; height: 32px; font-size: 14px; flex: none;">
                {{ (p.pax?.name || p.traveller?.name || p.name)?.charAt(0) || index + 1 }}
              </div>

              <div class="pax-info flex-grow-1 px-12 py-4 d-flex justify-content-between align-items-center overflow-hidden">
                <div class="text-truncate me-8">
                  <div class="text-sm fw-bold text-dark lh-1 mb-2">
                    {{ (p.pax?.name || p.traveller?.name || p.name) }}
                    {{ (p.pax?.surname || p.traveller?.surname || p.surname || "") }}
                    {{ (p.pax?.surname_1 || p.traveller?.surname_1 || p.surname_1 || "") }}
                    {{ (p.pax?.surname_2 || p.traveller?.surname_2 || p.surname_2 || "") }}
                  </div>
                  <div class="text-xs text-muted lh-1">{{ p.pax?.email || p.traveller?.email || p.email }}</div>
                </div>

                <div class="actions-container d-flex gap-2">
                  <v-button
                    class="btn-icon p-4 rounded-circle"
                    variant="icon"
                    @click="removePaxEntry(p, index)"
                  >
                    <v-icon icon="trash" size="xs" class="text-danger" />
                  </v-button>
                </div>
              </div>
            </div>

            <!-- Newly Added Pax (from search) -->
            <div v-for="(p, index) in paxFields" :key="'new-' + index" class="pax-item d-flex align-items-center mb-8 border border-primary rounded-xs overflow-hidden bg-white transition-all">
              <div class="pax-icon bg-primary text-white d-flex align-items-center justify-content-center fw-bold" style="width: 32px; height: 32px; font-size: 14px; flex: none;">
                {{ (p.pax?.name || p.traveller?.name || p.name)?.charAt(0) || '+' }}
              </div>

              <div class="pax-info flex-grow-1 px-12 py-4 d-flex justify-content-between align-items-center overflow-hidden">
                <div class="text-truncate me-8">
                  <div class="text-sm fw-bold text-primary lh-1 mb-2">
                    {{ (p.pax?.name || p.traveller?.name || p.name) }}
                    {{ (p.pax?.surname || p.traveller?.surname || p.surname || "") }}
                    {{ (p.pax?.surname_1 || p.traveller?.surname_1 || p.surname_1 || "") }}
                    {{ (p.pax?.surname_2 || p.traveller?.surname_2 || p.surname_2 || "") }}
                  </div>
                  <div class="text-xs text-muted lh-1">{{ p.pax?.email || p.traveller?.email || p.email }}</div>
                </div>

                <div class="actions-container d-flex gap-2">
                  <v-button
                    class="btn-icon p-4 rounded-circle"
                    variant="icon"
                    @click="removePaxEntry(p, index)"
                  >
                    <v-icon icon="trash" size="xs" class="text-danger" />
                  </v-button>
                </div>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <p class="m-16 text-sm fw-semi text-center">No pax available</p>
        </template>
        <fieldset class="mt-2">
          <div class="mb-0 search-separator-container">
            <div class="separator-text-container">
              <span class="text-sm separator-text fw-semi">You can search for a pax</span>
            </div>
          </div>
          <v-select-pax-search
            id="case-create-pax-search"
            v-model="paxId"
            :agency="agency?.slug || ''"
            :clearable="true"
            :is-disabled="!caseDay?.id"
            :return-object="true"
            :case-day="caseDay"
            @clear="handleClearClient"
            @update:modelValue="setPaxId"
            @reload-case="$emit('emitReloadCase')"
          />
          <div class="d-flex justify-content-end">
            <v-button
              v-if="isPax"
              class="btn-icon pax-save-btn"
              variant="primary"
              :disabled="!isFormValid || isSaving"
              @click="savePax"
            >
              {{ isSaving ? $t("general.button.saving") : $t("general.button.save") }}
              <v-icon icon="arrow-right" size="sm" space="ms-12" />
            </v-button>
          </div>
          <div class="separator-text-container mt-16">
            <v-checkbox
              v-if="dayPosition === 1"
              id="sync-pax"
              for-key="sync-pax"
              class="mb-8"
              :label="$t('general.shared.syncPax')"
              @update:modelValue="syncPax($event)"
            />
            <v-checkbox
              v-else
              id="copy-pax-from-previous-day"
              for-key="copy-pax-from-previous-day"
              :label="$t('general.shared.copypax')"
              @update:modelValue="copyPaxPriviousDay($event)"
            />
          </div>
        </fieldset>
      </OffCanvasBody>
    </ResizableContainer>
  </VOffCanvas>
</template>

<script setup lang="ts" name="TheCasePaxDetailsOffCanvas">
import { computed, onMounted, Ref, ref, watch } from "vue";
import VOffCanvas from "@/components/vendor/basic/offcanvas/VOffCanvas.vue";
import OffCanvasHeader from "@/components/vendor/basic/offcanvas/OffCanvasHeader.vue";
import OffCanvasBody from "@/components/vendor/basic/offcanvas/OffCanvasBody.vue";
import ResizableContainer from "@/components/resizablecontainer/ResizableContainer.vue";
import VSelectPaxSearch from "@/components/vendor/selectSearch/VSelectPaxSearch.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import { VCheckbox } from "@uniqoders/form";
import { VFormInput } from "@uniqoders/form";
import { useAuthStore } from "@/stores/auth";
import { storeToRefs } from "pinia";
import api from "@/api";
import { useToast } from "vue-toastification";
import * as yup from "yup";
import { useI18n } from "vue-i18n";

const emit = defineEmits(["close", "emitReloadCase"]);

const props = defineProps({
  caseDay: {
    type: Object,
    required: true,
  },
  dayPosition: {
    type: Number,
    required: true,
  },
  openDayId: {
    type: Number,
    required: true,
  }
});
const { t } = useI18n();
const toast = useToast();
const authStore = useAuthStore();
const { agency } = storeToRefs(authStore);
const paxId = ref(null);
const isPax = ref(false);
const availablePax: Ref<any[]> = ref([]);
const paxFields: Ref<any[]> = ref([]);
const paxErrors: Ref<any[]> = ref([]);
const isSaving = ref(false);

const sortPax = (a: any, b: any) => {
  const nameA = (a.pax?.name || a.traveller?.name || a.name || '').toLowerCase();
  const nameB = (b.pax?.name || b.traveller?.name || b.name || '').toLowerCase();
  if (nameA < nameB) return -1;
  if (nameA > nameB) return 1;
  const idA = a.traveller_id || a.pax?.id || a.id || 0;
  const idB = b.traveller_id || b.pax?.id || b.id || 0;
  return idA - idB;
};

const paxSchema = yup.object().shape({
  name: yup.string().trim().required("Name is required"),
  email: yup.string().trim().required("Email is required").email("Email must be a valid email"),
});

const isFormValid = computed(() => {
  return paxFields.value.every((p, index) => {
    const errors = paxErrors.value[index] || {};
    const hasErrors = Object.keys(errors).length > 0;
    return p.pax?.name?.trim() && p.pax?.email?.trim() && !hasErrors;
  });
});

const validatePax = async () => {
  let valid = true;
  paxErrors.value = [];

  for (const [index, p] of paxFields.value.entries()) {
    try {
      await paxSchema.validate(p.pax, { abortEarly: false });
      paxErrors.value[index] = {}; // no errors
    } catch (validationError: any) {
      valid = false;
      const fieldErrors: Record<string, string> = {};
      validationError.inner.forEach((err: any) => {
        fieldErrors[err.path] = err.message;
      });
      paxErrors.value[index] = fieldErrors;
    }
  }

  return valid;
};

onMounted(() => {
  if (props.caseDay?.paxes && Array.isArray(props.caseDay.paxes)) {
    availablePax.value = [...props.caseDay.paxes].sort(sortPax);
  } else {
    availablePax.value = [];
    paxErrors.value = [];
    isPax.value = false;
  }
});

watch(
  () => props.caseDay?.paxes,
  (newPaxes) => {
    if (Array.isArray(newPaxes)) {
      availablePax.value = [...newPaxes].sort(sortPax);
    } else {
      availablePax.value = [];
      paxErrors.value = [];
      isPax.value = false;
    }
  },
  { immediate: false, deep: true }
);

watch(
  paxFields,
  async (newFields) => {
    for (const [index, paxObj] of newFields.entries()) {
      // re-validate this pax
      try {
        await paxSchema.validate(paxObj.pax, { abortEarly: false });
        paxErrors.value[index] = {}; // clear errors if valid
      } catch (validationError: any) {
        const fieldErrors: Record<string, string> = {};
        validationError.inner.forEach((err: any) => {
          fieldErrors[err.path] = err.message;
        });
        paxErrors.value[index] = fieldErrors;
      }
    }
  },
  { deep: true } // ensures validation runs on every keypress
);

const setPaxId = async (val: any) => {
  if (!val) return;

  paxFields.value.push({
    pax: {
      name: val.name || "",
      surname: val.surname || "",
      surname_1: val.surname_1 || "",
      surname_2: val.surname_2 || "",
      email: val.email || "",
      id: val.id,
    },
  });
  paxErrors.value.push({});
  paxId.value = "";
  paxId.value = null; // clear input after adding
  isPax.value = true;
};

const handleClearClient = () => {
  paxId.value = null; // clears v-select correctly
};

const togglePaxField = () => {
  isPax.value = !isPax.value;
  if (isPax.value && paxFields.value.length === 0) {
    addPaxEntry();
  }
};

const addPaxEntry = () => {
  paxFields.value.push({
    pax: {
      name: "",
      email: "",
    },
  });
  paxErrors.value.push({});
};

const removePaxEntry = async (p: any, index: number) => {
  const paxIdToRemove = p.traveller_id || p.pax?.id;

  // Check if this pax is in availablePax (already saved to backend)
  const availIndex = availablePax.value.findIndex(avail => (avail.traveller_id || avail.pax?.id) === paxIdToRemove);

  if (availIndex > -1) {
    // Pax is already saved — call API to remove
    try {
      const res = await api.case.caseDayItemRemovePax(
        agency.value?.slug,
        props.caseDay?.case_id,
        props.caseDay?.id,
        paxIdToRemove
      );
      toast.success(res.message);
      availablePax.value.splice(availIndex, 1);
      emit("emitReloadCase");
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t("general.shared.somethingWentWrong"));
    }
  } else {
    // Pax is in paxFields (not yet saved) — just remove locally
    const fieldsIndex = paxFields.value.findIndex(field => field === p);
    if (fieldsIndex > -1) {
      paxFields.value.splice(fieldsIndex, 1);
      paxErrors.value.splice(fieldsIndex, 1);
    }
  }
};

const closeOffCanvas = () => {
  emit("close");
  isPax.value = false;
  paxFields.value = [];
  paxErrors.value = [];
  paxId.value = null;
};

const savePax = async () => {
  const valid = await validatePax();
  if (!valid) {
    return;
  }

  isSaving.value = true;
  try {
    for (const [index, p] of paxFields.value.entries()) {
      try {
        await paxSchema.validate(p.pax, { abortEarly: false });
      } catch (validationError: any) {
        validationError.errors.forEach((msg: string) => {
          toast.error(`Pax ${index + 1}: ${msg}`);
        });
        return; // stop saving if any validation fails
      }
    }

    const promises = paxFields.value.map(async (p: any) => {
      if (!p.traveller_id) {
        let payload;

        if (p.pax?.id) {
          payload = { traveller_id: p.pax.id };
        } else {
          payload = {
            name: p.pax?.name,
            email: p.pax?.email,
          };
        }

        try {
          const res = await api.case.caseDayItemAddPax(
            agency.value?.slug,
            props.caseDay?.case_id,
            props.caseDay?.id,
            payload
          );

          toast.success(res.message);
        } catch (error: any) {
          console.error("Failed to add pax:", error);

          const paxName = p.pax?.name || "Unknown";
          const paxEmail = p.pax?.email ? ` (${p.pax.email})` : "";

          toast.error(`Failed to add pax: ${paxName}${paxEmail}. ${error.message}`);
        }
      }
    });

    await Promise.all(promises);
    paxFields.value = [];
    isPax.value = false;
    emit("emitReloadCase");
  } catch (error) {
    console.error("Error in savePax:", error);
    toast.error("An unexpected error occurred while saving pax.");
  } finally {
    isSaving.value = false;
  }
};

const syncPax = async (val: any) => {
  if (val) {
    try {
      await api.case.syncPaxCaseAllDay(agency.value?.slug, props.caseDay?.case_id, props.openDayId);
      toast.success(t("general.shared.syncPaxMessage"));
      emit("emitReloadCase");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong while syncing pax.");
    }
  }
};

const copyPaxPriviousDay = async (val: any) => {
  if (val) {
    try {
      await api.case.copyPaxFromPreviousDay(agency.value?.slug, props.caseDay?.case_id, props.openDayId);
      toast.success(t("general.shared.copyPaxMessage"));
      emit("emitReloadCase");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong while syncing pax.");
    }
  }
};
</script>

<style scoped>
.pax-item {
  transition: all 0.2s ease;
  &:hover {
    background-color: var(--uq-light) !important;
    border-color: var(--uq-primary) !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  }
}

.flex-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.icon-plus {
  font-size: 0.6rem;
  margin-top: 1.5px;
}

.rounded-circle {
  height: 1.1rem;
  width: 1.1rem;
}

.pax-save-btn {
  padding: 10px;
}

.field-error {
  display: block;
  min-height: 1.125rem;
  margin-top: 6px;
  font-size: 0.8rem;
  line-height: 1.125rem;
  color: transparent;
  transition: color 0.12s ease;
  visibility: hidden;
}

.field-error.visible {
  color: #dc3545;
  visibility: visible;
}

.is-invalid,
.v-form-input.is-invalid input {
  border-color: #dc3545 !important;
  box-shadow: none !important;
}
</style>
